<script>
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_EXTENSIONS = [".xlsx", ".xls"];
const ALLOWED_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

export default {
  page: {
    title: "Import Blueprint",
    meta: [{ name: "description", content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
  },

  data() {
    return {
      title: "Import Blueprint",
      items: [
        { text: "Tools Asesmen", href: "/master-asesmen/tools-asesmen" },
        { text: "Import Blueprint", active: true },
      ],

      // File state
      selectedFile: null,
      isDragOver: false,

      // Upload state
      isUploading: false,
      uploadProgress: 0,

      // Validation
      fileError: null,

      // Preview state
      previewData: [],
      previewHeaders: [],
      showPreview: false,
      isParsingFile: false,
      MAX_FILE_SIZE_MB: MAX_FILE_SIZE_MB,

      // Result state
      importResult: null,
    };
  },

  computed: {
    idKompetensi() {
      return this.$route.params.id;
    },

    fileSizeDisplay() {
      if (!this.selectedFile) return "";
      const kb = this.selectedFile.size / 1024;
      if (kb < 1024) return `${kb.toFixed(1)} KB`;
      return `${(kb / 1024).toFixed(2)} MB`;
    },

    isFileReady() {
      return this.selectedFile !== null && this.fileError === null;
    },
  },

  methods: {
    // ─── File Selection ───────────────────────────────────────────────────────

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    onFileInputChange(event) {
      const file = event.target.files[0];
      if (file) this.handleFileSelected(file);
      event.target.value = "";
    },

    onDragOver(event) {
      event.preventDefault();
      this.isDragOver = true;
    },

    onDragLeave() {
      this.isDragOver = false;
    },

    onDrop(event) {
      event.preventDefault();
      this.isDragOver = false;
      const file = event.dataTransfer.files[0];
      if (file) this.handleFileSelected(file);
    },

    handleFileSelected(file) {
      this.resetState();

      const validationError = this.validateFile(file);
      if (validationError) {
        this.fileError = validationError;
        return;
      }

      this.selectedFile = file;
      this.parseExcelPreview(file);
    },

    // ─── Validation ───────────────────────────────────────────────────────────

    validateFile(file) {
      if (!file) return "Tidak ada file yang dipilih.";

      const extension = "." + file.name.split(".").pop().toLowerCase();
      const isValidExtension = ALLOWED_EXTENSIONS.includes(extension);
      const isValidMime =
        ALLOWED_MIME_TYPES.includes(file.type) || file.type === "";

      if (!isValidExtension || !isValidMime) {
        return `Format file tidak didukung. Hanya menerima file Excel (${ALLOWED_EXTENSIONS.join(
          ", "
        )}).`;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        return `Ukuran file melebihi batas maksimal ${MAX_FILE_SIZE_MB}MB. Ukuran file Anda: ${(
          file.size /
          1024 /
          1024
        ).toFixed(2)}MB.`;
      }

      return null;
    },

    // ─── Excel Preview ────────────────────────────────────────────────────────

    async parseExcelPreview(file) {
      this.isParsingFile = true;
      this.showPreview = false;

      try {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        });

        if (jsonData.length === 0) {
          this.fileError = "File Excel kosong atau tidak memiliki data.";
          this.selectedFile = null;
          return;
        }

        // Header blueprint ada di baris ke-12 (index 11)
        const headerRowIndex = jsonData.length > 11 ? 11 : 0;
        this.previewHeaders = jsonData[headerRowIndex] || [];
        this.previewData = jsonData.slice(headerRowIndex + 2, headerRowIndex + 12);
        this.showPreview = true;
      } catch (err) {
        console.error("Error parsing Excel:", err);
        this.fileError =
          "Gagal membaca file Excel. Pastikan file tidak rusak atau terenkripsi.";
        this.selectedFile = null;
      } finally {
        this.isParsingFile = false;
      }

      if (this.selectedFile) {
        await this.uploadFile();
      }
    },

    // ─── Upload ───────────────────────────────────────────────────────────────

    async uploadFile() {
      if (!this.isFileReady || this.isUploading) return;

      this.isUploading = true;
      this.uploadProgress = 0;
      this.importResult = null;

      const formData = new FormData();
      formData.append("file", this.selectedFile);
      formData.append("idkompetensi", this.idKompetensi);
      if (this.$route.query.level) {
        formData.append("level", this.$route.query.level);
      }

      try {
        const response = await axios({
          method: "post",
          url:
            process.env.VUE_APP_BACKEND_URL_VERSION +
            `import-excel/blueprint/${this.idKompetensi}`,
          params: this.$route.query.level
            ? { level: this.$route.query.level }
            : {},
          data: formData,
          headers: {
            Authorization: `Bearer ${localStorage.access_token}`,
            Accept: "application/json",
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              this.uploadProgress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            }
          },
        });

        const resData = response.data;
        const stats = resData?.data || null;
        const rowErrors = (stats?.errors || []).map((e) => ({
          field: e.row ? `Baris ${e.row}` : null,
          message: e.message,
        }));

        const hasFailedRows = (stats?.total_gagal || 0) > 0 || (stats?.status === false);

        this.importResult = {
          success: !hasFailedRows,
          message:
            resData?.meta?.message ||
            resData?.message ||
            (hasFailedRows
              ? "Import blueprint selesai dengan catatan."
              : "Import blueprint berhasil!"),
          stats,
          errors: rowErrors,
        };

        Swal.fire({
          icon: hasFailedRows ? "warning" : "success",
          title: hasFailedRows ? "Import Selesai dengan Catatan" : "Import Berhasil!",
          html: hasFailedRows
            ? `${this.importResult.message}<br><small>${stats.total_sukses || 0} berhasil, ${stats.total_gagal || 0} gagal.</small>`
            : this.importResult.message,
          confirmButtonColor: hasFailedRows ? "#f1b44c" : "#34c38f",
        });
      } catch (error) {
        const status = error.response?.status;
        const resData = error.response?.data;

        let errorMessage =
          resData?.meta?.message ||
          resData?.message ||
          "Terjadi kesalahan saat mengimport blueprint.";

        let validationErrors = [];
        if (status === 422 && resData?.errors) {
          validationErrors = Object.entries(resData.errors).flatMap(
            ([field, messages]) =>
              messages.map((msg) => ({ field, message: msg }))
          );
        } else if (resData?.data?.errors) {
          validationErrors = resData.data.errors.map((e) => ({
            field: e.row ? `Baris ${e.row}` : (e.field || null),
            message: e.message || e,
          }));
        }

        this.importResult = {
          success: false,
          message: errorMessage,
          errors: validationErrors,
          stats: null,
        };

        Swal.fire({
          icon: "error",
          title: "Import Gagal",
          text: errorMessage,
          confirmButtonColor: "#f46a6a",
        });
      } finally {
        this.isUploading = false;
      }
    },

    // ─── Helpers ──────────────────────────────────────────────────────────────

    downloadTemplate() {
      const levelParam = this.$route.query.level
        ? `?level=${this.$route.query.level}`
        : "";
      window.open(
        process.env.VUE_APP_BACKEND_URL_VERSION +
          "export-excel/blueprint/" +
          this.idKompetensi +
          levelParam,
        "_blank"
      );
    },

    resetState() {
      this.selectedFile = null;
      this.fileError = null;
      this.uploadProgress = 0;
      this.isUploading = false;
      this.previewData = [];
      this.previewHeaders = [];
      this.showPreview = false;
      this.importResult = null;
      this.isParsingFile = false;
    },

    removeFile() {
      this.resetState();
    },

    goBack() {
      this.$router.push("/master-asesmen/tools-asesmen");
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div
            class="card-header d-flex align-items-center justify-content-between"
          >
            <h5 class="mb-0">
              <i class="fa fa-file-import me-2 text-primary"></i>
              Import Blueprint dari Excel
            </h5>
            <button class="btn btn-secondary btn-sm" @click="goBack">
              <i class="fa fa-arrow-left me-1"></i> Kembali
            </button>
          </div>

          <div class="card-body">
            <!-- ── INFO TEMPLATE ──────────────────────────────────────── -->
            <div class="alert alert-info d-flex align-items-start gap-3 mb-4">
              <i class="fa fa-circle-info fa-lg mt-1"></i>
              <div>
                <strong>Panduan Import Blueprint</strong>
                <ul class="mb-1 mt-1 ps-3">
                  <li>
                    Format file yang didukung: <code>.xlsx</code>,
                    <code>.xls</code>
                  </li>
                  <li>
                    Maksimal ukuran file:
                    <strong>{{ MAX_FILE_SIZE_MB }}MB</strong>
                  </li>
                  <li>
                    Pastikan file sesuai dengan format template yang telah disediakan
                  </li>
                  <li>File akan diproses otomatis setelah dipilih</li>
                </ul>
                <a
                  href="#"
                  class="btn btn-outline-info btn-sm mt-1"
                  @click.prevent="downloadTemplate"
                >
                  <i class="fa fa-download me-1"></i> Download Template Excel
                </a>
              </div>
            </div>

            <!-- ── DROP ZONE ──────────────────────────────────────────── -->
            <div
              class="drop-zone"
              :class="{
                'drop-zone--active': isDragOver,
                'drop-zone--filled': selectedFile && !fileError,
                'drop-zone--error': fileError,
                'drop-zone--uploading': isUploading,
              }"
              @click="!isUploading && triggerFileInput()"
              @dragover="onDragOver"
              @dragleave="onDragLeave"
              @drop="onDrop"
            >
              <input
                ref="fileInput"
                type="file"
                accept=".xlsx,.xls"
                class="d-none"
                @change="onFileInputChange"
                :disabled="isUploading"
              />

              <!-- State: Idle / Drag -->
              <template v-if="!selectedFile && !isParsingFile">
                <div class="drop-zone__content">
                  <i
                    class="fa fa-cloud-arrow-up drop-zone__icon"
                    :class="isDragOver ? 'text-primary' : 'text-muted'"
                  ></i>
                  <p class="drop-zone__text mb-1">
                    <span v-if="isDragOver">Lepaskan file di sini</span>
                    <span v-else>
                      <strong>Klik untuk pilih file</strong> atau seret & lepas
                      ke sini
                    </span>
                  </p>
                  <p class="text-muted small mb-0">
                    Excel (.xlsx, .xls) — Maks. {{ MAX_FILE_SIZE_MB }}MB
                  </p>
                </div>
              </template>

              <!-- State: Parsing -->
              <template v-else-if="isParsingFile">
                <div class="drop-zone__content">
                  <i class="fa fa-spinner fa-spin fa-2x text-primary mb-2"></i>
                  <p class="mb-0">Membaca file...</p>
                </div>
              </template>

              <!-- State: File ready -->
              <template v-else-if="selectedFile">
                <div class="drop-zone__content">
                  <i class="fa fa-file-excel fa-3x text-success mb-2"></i>
                  <p class="fw-semibold mb-0">{{ selectedFile.name }}</p>
                  <p class="text-muted small mb-0">{{ fileSizeDisplay }}</p>
                </div>
              </template>
            </div>

            <!-- ── FILE ERROR ─────────────────────────────────────────── -->
            <div
              v-if="fileError"
              class="alert alert-danger mt-3 d-flex align-items-center gap-2"
            >
              <i class="fa fa-circle-exclamation"></i>
              <span>{{ fileError }}</span>
            </div>

            <!-- ── UPLOAD PROGRESS ────────────────────────────────────── -->
            <div v-if="isUploading" class="mt-4">
              <div class="d-flex justify-content-between mb-1">
                <span class="small fw-semibold">Mengunggah file...</span>
                <span class="small text-muted">{{ uploadProgress }}%</span>
              </div>
              <div class="progress" style="height: 8px">
                <div
                  class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                  role="progressbar"
                  :style="{ width: uploadProgress + '%' }"
                ></div>
              </div>
            </div>

            <!-- ── SELECTED FILE INFO + ACTION ────────────────────────── -->
            <div
              v-if="selectedFile && !isUploading"
              class="d-flex align-items-center justify-content-between mt-3 p-3 bg-light rounded border"
            >
              <div class="d-flex align-items-center gap-2">
                <i class="fa fa-paperclip text-muted"></i>
                <span class="fw-semibold">{{ selectedFile.name }}</span>
                <span class="badge bg-secondary">{{ fileSizeDisplay }}</span>
              </div>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-outline-danger btn-sm"
                  @click="removeFile"
                >
                  <i class="fa fa-trash me-1"></i> Hapus
                </button>
                <button
                  class="btn btn-primary btn-sm"
                  @click="uploadFile"
                  :disabled="isUploading"
                >
                  <i class="fa fa-upload me-1"></i> Upload Ulang
                </button>
              </div>
            </div>

            <!-- ── IMPORT RESULT SUMMARY ──────────────────────────────── -->
            <div v-if="importResult" class="mt-4">
              <!-- Alert Utama -->
              <div
                class="alert d-flex align-items-start gap-2"
                :class="
                  importResult.success ? 'alert-success' : 'alert-danger'
                "
              >
                <i
                  class="fa mt-1"
                  :class="
                    importResult.success
                      ? 'fa-circle-check text-success'
                      : 'fa-circle-xmark text-danger'
                  "
                ></i>
                <div class="flex-grow-1">
                  <strong>{{
                    importResult.success ? "Sukses!" : "Gagal!"
                  }}</strong>
                  <p class="mb-0">{{ importResult.message }}</p>

                  <!-- Stat badges jika ada -->
                  <div
                    v-if="importResult.stats"
                    class="d-flex gap-2 mt-2 flex-wrap"
                  >
                    <span class="badge bg-primary">
                      Total Data: {{ importResult.stats.total_soal || 0 }}
                    </span>
                    <span class="badge bg-success">
                      Berhasil Update: {{ importResult.stats.total_sukses || 0 }}
                    </span>
                    <span
                      v-if="(importResult.stats.total_gagal || 0) > 0"
                      class="badge bg-danger"
                    >
                      Gagal: {{ importResult.stats.total_gagal }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Daftar Error Baris jika ada -->
              <div
                v-if="importResult.errors && importResult.errors.length > 0"
                class="card border-danger mt-3"
              >
                <div class="card-header bg-danger text-white py-2">
                  <h6 class="mb-0">
                    <i class="fa fa-triangle-exclamation me-1"></i>
                    Detail Kesalahan ({{ importResult.errors.length }})
                  </h6>
                </div>
                <div class="card-body p-0">
                  <div class="table-responsive" style="max-height: 250px">
                    <table class="table table-sm table-striped mb-0">
                      <thead class="table-light sticky-top">
                        <tr>
                          <th style="width: 120px">Lokasi</th>
                          <th>Keterangan Kesalahan</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(err, idx) in importResult.errors"
                          :key="idx"
                        >
                          <td class="text-danger fw-semibold">
                            {{ err.field || `Item #${idx + 1}` }}
                          </td>
                          <td class="text-danger">{{ err.message }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── PREVIEW TABEL EXCEL ────────────────────────────────── -->
            <div v-if="showPreview && previewData.length > 0" class="mt-4">
              <h6 class="text-muted mb-2">
                <i class="fa fa-table me-1"></i>
                Preview Data (10 baris pertama)
              </h6>
              <div class="table-responsive border rounded" style="max-height: 300px">
                <table class="table table-bordered table-sm table-hover mb-0 text-nowrap">
                  <thead class="table-light sticky-top">
                    <tr>
                      <th
                        v-for="(header, hIdx) in previewHeaders"
                        :key="hIdx"
                        class="small fw-semibold"
                      >
                        {{ header || `Kolom ${hIdx + 1}` }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, rIdx) in previewData" :key="rIdx">
                      <td
                        v-for="(cell, cIdx) in row"
                        :key="cIdx"
                        class="small text-truncate"
                        style="max-width: 200px"
                      >
                        {{ cell }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.drop-zone {
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  background-color: #f8fafc;
}

.drop-zone:hover,
.drop-zone--active {
  border-color: #6f42c1;
  background-color: #f3f0fc;
}

.drop-zone--filled {
  border-color: #198754;
  background-color: #f0fdf4;
}

.drop-zone--error {
  border-color: #dc3545;
  background-color: #fff5f5;
}

.drop-zone--uploading {
  cursor: not-allowed;
  opacity: 0.7;
}

.drop-zone__content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.drop-zone__icon {
  font-size: 2.5rem;
  margin-bottom: 12px;
}
</style>
